/**
 * Browser-side WS subscriber to data-api `/ws/live`.
 *
 * - Capped exponential backoff (500ms..30s).
 * - Validates every frame against the LiveEvent Zod schema; drops + counts
 *   anything that doesn't parse.
 * - Exposes `connected` / `lastEventTs` / `droppedInvalid` for the UI.
 *
 * Usage: `new LiveSocket(url, onEvent).connect()` — caller owns the
 * lifecycle and disposes via `.close()`.
 */
import { LiveEvent } from "./schemas";

const BACKOFF_MS = [500, 1000, 2000, 5000, 10_000, 20_000, 30_000];

export type LiveListener = (evt: LiveEvent) => void;
export type StateListener = (state: LiveSocketState) => void;

export interface LiveSocketState {
  connected: boolean;
  reconnectAttempt: number;
  lastEventTs: string | null;
  droppedInvalid: number;
  lastError: string | null;
}

export interface LiveSocketOpts {
  url: string;
  onEvent: LiveListener;
  onState?: StateListener;
  WebSocketImpl?: typeof WebSocket;
  /** Override backoff for tests. */
  backoffMs?: readonly number[];
  /** When true, do not auto-reconnect (used in tests). */
  manual?: boolean;
}

export class LiveSocket {
  private ws: WebSocket | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  private state: LiveSocketState = {
    connected: false,
    reconnectAttempt: 0,
    lastEventTs: null,
    droppedInvalid: 0,
    lastError: null,
  };

  constructor(private readonly opts: LiveSocketOpts) {}

  connect(): void {
    this.stopped = false;
    this.openOnce();
  }

  close(): void {
    this.stopped = true;
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    if (this.ws) {
      try { this.ws.close(); } catch {}
      this.ws = null;
    }
    this.update({ connected: false });
  }

  getState(): LiveSocketState { return { ...this.state }; }

  private openOnce() {
    if (this.stopped) return;
    const Impl = this.opts.WebSocketImpl ?? globalThis.WebSocket;
    const sock = new Impl(this.opts.url);
    this.ws = sock as unknown as WebSocket;
    sock.addEventListener("open", () => {
      this.update({ connected: true, reconnectAttempt: 0, lastError: null });
    });
    sock.addEventListener("message", (evt: MessageEvent) => {
      let parsed: unknown;
      try { parsed = JSON.parse(typeof evt.data === "string" ? evt.data : ""); }
      catch {
        this.update({ droppedInvalid: this.state.droppedInvalid + 1 });
        return;
      }
      const ok = LiveEvent.safeParse(parsed);
      if (!ok.success) {
        this.update({ droppedInvalid: this.state.droppedInvalid + 1 });
        return;
      }
      this.update({ lastEventTs: ok.data.ts });
      try { this.opts.onEvent(ok.data); } catch (err) {
        this.update({ lastError: (err as Error).message });
      }
    });
    sock.addEventListener("close", () => {
      this.update({ connected: false });
      this.scheduleReconnect();
    });
    sock.addEventListener("error", (e: Event) => {
      const msg = (e as ErrorEvent)?.message ?? "ws error";
      this.update({ lastError: msg });
    });
  }

  private scheduleReconnect() {
    if (this.stopped || this.opts.manual) return;
    const schedule = this.opts.backoffMs ?? BACKOFF_MS;
    const attempt = this.state.reconnectAttempt;
    const idx = Math.min(attempt, schedule.length - 1);
    const delay = schedule[idx]!;
    this.update({ reconnectAttempt: attempt + 1 });
    this.timer = setTimeout(() => this.openOnce(), delay);
  }

  private update(patch: Partial<LiveSocketState>) {
    this.state = { ...this.state, ...patch };
    this.opts.onState?.({ ...this.state });
  }
}
